import { ErrorHandler, Injectable, Injector } from "@angular/core";
import { NavigationStart, Router } from "@angular/router";
import * as Sentry from "@sentry/angular";

const RELOAD_GUARD_KEY = "chunkLoadErrorReloadAt";
const RELOAD_GUARD_WINDOW_MS = 60_000;

function isChunkLoadError(error: any): boolean {
  const err = error?.rejection ?? error;
  return (
    err?.name === "ChunkLoadError" ||
    /Loading chunk .* failed/i.test(err?.message ?? "")
  );
}

/**
 * Safety net for stale lazy-chunk 404s (FLIP-48): a tab left open across a deploy holds an
 * index.html/main.js referencing the previous build's hashed chunk filenames. Navigating to a
 * lazy route not yet loaded in that tab then 404s on import() and the router navigation just
 * dies silently (nothing subscribes to NavigationError). Detecting the ChunkLoadError here and
 * reloading onto the attempted route recovers onto the new build instead of leaving the click
 * looking like it did nothing.
 */
@Injectable()
export class ChunkLoadErrorHandler implements ErrorHandler {
  private readonly sentryHandler = Sentry.createErrorHandler();
  private lastAttemptedUrl = "/";
  private routerSubscribed = false;

  constructor(private injector: Injector) {}

  handleError(error: any): void {
    this.sentryHandler.handleError(error);
    this.ensureRouterSubscription();

    if (!isChunkLoadError(error)) {
      return;
    }

    const lastReload = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) || 0);
    if (Date.now() - lastReload < RELOAD_GUARD_WINDOW_MS) {
      return;
    }
    sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));

    window.location.assign(this.lastAttemptedUrl);
  }

  // Deferred rather than resolved in the constructor - ErrorHandler is constructed very early in
  // bootstrap, and eagerly injecting Router there risks a circular/NullInjector error. By the
  // time the first error reaches handleError(), the router is safely resolvable.
  private ensureRouterSubscription(): void {
    if (this.routerSubscribed) {
      return;
    }
    try {
      const router = this.injector.get(Router);
      router.events.subscribe((event) => {
        if (event instanceof NavigationStart) {
          this.lastAttemptedUrl = event.url;
        }
      });
      this.routerSubscribed = true;
    } catch {
      // Router not resolvable yet - retry on the next error.
    }
  }
}
